import React, { useMemo, useState } from 'react'
import ConfirmModal from 'pages/company/SwmsManagement/ConfirmModal'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import {
  IconBuilding,
  IconPencil,
  IconUserShield,
  IconUsers,
  IconHelmet,
  IconPlus,
  IconTrash,
  IconMailForward,
} from '@tabler/icons-react'
import { PageTitle } from 'context/PageTitleContext'
import {
  getClientById,
  getClientEmployees,
  getCompanyUsers,
  getCompanySupervisors,
  deleteCompanyUser,
  deleteSupervisor,
  resendSupervisorWelcomeEmail,
} from 'services/clients.svc'
import { useParamountAuth } from 'context/ParamountAuthContext'
import { Roles } from '@paramount-prestart/shared'
import './Company.scss'

const { CompanyAdmin: ROLE_COMPANY_ADMIN, CompanyUser: ROLE_COMPANY_USER } = Roles.ROLES

// ── Helpers ───────────────────────────────────────────────────────────────────

function initials(first, last) {
  return `${first?.[0] ?? ''}${last?.[0] ?? ''}`.toUpperCase()
}

function idToDate(id) {
  return new Date(parseInt(id.substring(0, 8), 16) * 1000)
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionCard({ icon, title, action, children, flush }) {
  return (
    <div className="cp-card">
      <div className="cp-card-header">
        <span className="cp-card-title">
          {icon}
          {title}
        </span>
        {action}
      </div>
      <div className={`cp-card-body${flush ? ' cp-card-body--flush' : ''}`}>
        {children}
      </div>
    </div>
  )
}

function AdminChip({ employee }) {
  return (
    <div className="cp-admin-chip">
      <div className="cp-admin-chip-avatar">{initials(employee.firstName, employee.surname)}</div>
      <div className="cp-admin-chip-info">
        <span className="cp-admin-chip-name">{employee.firstName} {employee.surname}</span>
        {employee.contactEmail && (
          <span className="cp-admin-chip-email">{employee.contactEmail}</span>
        )}
      </div>
    </div>
  )
}

function TableHeader({ showDelete, showStatus }) {
  return (
    <div className={[
      'cp-person-row cp-person-row--header',
      showStatus && 'cp-person-row--status',
      showDelete && 'cp-person-row--deletable',
    ].filter(Boolean).join(' ')}>
      {showStatus && <span />}
      <span>Name</span>
      <span>Email</span>
      <span>Added On</span>
      {showDelete && <span />}
    </div>
  )
}

function PersonRow({ employee, onDelete, showStatus }) {
  const accepted = showStatus && !!employee.userRole?.termsAcceptedAt
  const addedOn = idToDate(employee._id).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  return (
    <div className={[
      'cp-person-row',
      showStatus && 'cp-person-row--status',
      showStatus && (accepted ? 'cp-person-row--accepted' : 'cp-person-row--pending'),
      onDelete && 'cp-person-row--deletable',
    ].filter(Boolean).join(' ')}>
      {showStatus && <span className={`cp-dot cp-dot--${accepted ? 'accepted' : 'pending'}`} />}
      <span className="cp-person-name">
        {employee.firstName} {employee.surname}
      </span>
      <span className="cp-person-email">{employee.contactEmail}</span>
      <span className="cp-person-date">{addedOn}</span>
      {onDelete && (
        <button className="cp-delete-btn" onClick={onDelete} aria-label="Delete user">
          <IconTrash size={13} stroke={2} />
        </button>
      )}
    </div>
  )
}

function SupervisorTableHeader({ showActions }) {
  return (
    <div className={['cp-sv-row cp-sv-row--header', showActions && 'cp-sv-row--deletable'].filter(Boolean).join(' ')}>
      <span>First Name</span>
      <span>Surname</span>
      <span>Email Address</span>
      <span>Phone Number</span>
      <span>Emergency Contact</span>
      <span>Emergency No.</span>
      <span>Added On</span>
      {showActions && <span />}
    </div>
  )
}

function SupervisorRow({ employee, onResend, onDelete }) {
  const addedOn = idToDate(employee._id).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  })

  return (
    <div className={[
      'cp-sv-row',
      (onResend || onDelete) && 'cp-sv-row--deletable',
    ].filter(Boolean).join(' ')}>
      <span className="cp-person-name">{employee.firstName}</span>
      <span className="cp-person-name">{employee.surname}</span>
      <span className="cp-person-email">{employee.contactEmail}</span>
      <span className="cp-person-email">{employee.phone || '—'}</span>
      <span className="cp-person-email">{employee.emergencyContactName || '—'}</span>
      <span className="cp-person-email">{employee.emergencyContactPhone || '—'}</span>
      <span className="cp-person-date">{addedOn}</span>
      {(onResend || onDelete) && (
        <span className="cp-sv-actions">
          {onResend && (
            <button
              className="cp-delete-btn"
              onClick={onResend}
              aria-label="Resend invite email"
              data-tooltip="Resend invite email"
              data-placement="left"
            >
              <IconMailForward size={13} stroke={2} />
            </button>
          )}
          {onDelete && (
            <button className="cp-delete-btn" onClick={onDelete} aria-label="Delete supervisor">
              <IconTrash size={13} stroke={2} />
            </button>
          )}
        </span>
      )}
    </div>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function CompanyProfile() {
  const { companyId } = useParams()
  const { userRoles } = useParamountAuth()
  const isAdmin = userRoles?.includes(ROLE_COMPANY_ADMIN)
  const canResendInvite = isAdmin || userRoles?.includes(ROLE_COMPANY_USER)
  const queryClient = useQueryClient()

  const { data: company, isLoading: loadingCompany } = useQuery(
    ['company', companyId],
    () => getClientById(companyId),
  )

  const { data: employees = [] } = useQuery(
    ['clientEmployees', companyId],
    () => getClientEmployees(companyId),
  )

  const { data: companyUsers = [], isLoading: loadingCompanyUsers, isError: errorCompanyUsers } = useQuery(
    ['companyUsers', companyId],
    () => getCompanyUsers(companyId),
  )

  const { data: supervisors = [], isLoading: loadingSupervisors, isError: errorSupervisors } = useQuery(
    ['companySupervisors', companyId],
    () => getCompanySupervisors(companyId),
  )

  const sortedSupervisors = useMemo(() => {
    return [...supervisors].sort((a, b) => {
      const aAccepted = !!a.userRole?.termsAcceptedAt
      const bAccepted = !!b.userRole?.termsAcceptedAt
      if (aAccepted !== bAccepted) return aAccepted ? -1 : 1
      return a._id < b._id ? -1 : 1
    })
  }, [supervisors])

  const admins = useMemo(
    () => employees.filter((e) => e.userRole?.role === ROLE_COMPANY_ADMIN),
    [employees],
  )

  const [pendingDelete, setPendingDelete] = useState(null)

  const { mutate: removeCompanyUser } = useMutation({
    mutationFn: (userDetailId) => deleteCompanyUser(companyId, userDetailId),
    onSuccess: () => {
      toast.success('Company user removed')
      queryClient.invalidateQueries({ queryKey: ['companyUsers', companyId] })
      setPendingDelete(null)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to remove user')
      setPendingDelete(null)
    },
  })

  const { mutate: resendInvite } = useMutation({
    mutationFn: ({ userRoleId }) => resendSupervisorWelcomeEmail(companyId, userRoleId),
    onSuccess: (_, { email }) => toast.success(`Invite email resent to ${email}`),
    onError: (err) => toast.error(err.message || 'Failed to resend invite email'),
  })

  const { mutate: removeSupervisor } = useMutation({
    mutationFn: (userDetailId) => deleteSupervisor(companyId, userDetailId),
    onSuccess: () => {
      toast.success('Supervisor removed')
      queryClient.invalidateQueries({ queryKey: ['companySupervisors', companyId] })
      setPendingDelete(null)
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to remove supervisor')
      setPendingDelete(null)
    },
  })

  const handleConfirmDelete = () => {
    if (!pendingDelete) return
    if (pendingDelete.type === 'companyUser') removeCompanyUser(pendingDelete.id)
    else removeSupervisor(pendingDelete.id)
  }

  if (loadingCompany) {
    return (
      <div className="cp-root">
        <PageTitle title="Company Profile" />
        <div className="cp-loading">Loading, please wait…</div>
      </div>
    )
  }

  const deleteConfirm = pendingDelete && {
    type: 'delete',
    title: 'Remove user',
    label: pendingDelete.name,
    message: pendingDelete.type === 'companyUser'
      ? 'This user will no longer be able to manage company tasks or access the portal.'
      : 'This user will no longer be able to check in to your sites as a supervisor.',
  }

  return (
    <>
    <div className="cp-root">
      <PageTitle title="Company Profile" />

      {/* ── Hero banner ───────────────────────────────────────────────────── */}
      <div className="cp-hero">
        <div className="cp-hero-inner">
          <div className="cp-hero-left">
            {company?.image?.url ? (
              <img
                src={company.image.url}
                alt={`${company.name} logo`}
                className="cp-hero-logo"
              />
            ) : (
              <div className="cp-hero-logo-placeholder">
                <IconBuilding size={28} stroke={1.5} />
              </div>
            )}
            <div className="cp-hero-body">
              <h1 className="cp-hero-name">{company?.name}</h1>
              <div className="cp-hero-fields">
                {company?.abn && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">ABN</span>
                    <span className="cp-hero-field-value">{company.abn}</span>
                  </div>
                )}
                {company?.buildersRegNumber && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">Builders Reg. No.</span>
                    <span className="cp-hero-field-value">{company.buildersRegNumber}</span>
                  </div>
                )}
                {company?.email && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">Office Email</span>
                    <span className="cp-hero-field-value">{company.email}</span>
                  </div>
                )}
                {company?.phone && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">Office Phone</span>
                    <span className="cp-hero-field-value">{company.phone}</span>
                  </div>
                )}
                {company?.website && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">Website</span>
                    <span className="cp-hero-field-value">{company.website}</span>
                  </div>
                )}
                {company?.address && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">Office Address</span>
                    <span className="cp-hero-field-value">{company.address}</span>
                  </div>
                )}
                {company?.postalAddress && (
                  <div className="cp-hero-field">
                    <span className="cp-hero-field-label">Postal Address</span>
                    <span className="cp-hero-field-value">{company.postalAddress}</span>
                  </div>
                )}
              </div>
              {admins.length > 0 && (
                <div className="cp-managed-by">
                  <span className="cp-managed-by-label">
                    <IconUserShield size={13} stroke={1.8} />
                    Managed by
                  </span>
                  <div className="cp-managed-by-chips">
                    {admins.map((e) => (
                      <AdminChip key={e._id} employee={e} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {isAdmin && (
            <Link to="edit" className="cp-edit-btn">
              <IconPencil size={14} stroke={2} />
              Edit Information
            </Link>
          )}
        </div>
      </div>

      <div className="cp-content">

        {/* ── Company Users ──────────────────────────────────────────────── */}
        <SectionCard
          icon={<IconUsers size={15} stroke={1.5} />}
          title="Company Users"
          flush
          action={
            isAdmin && (
              <Link to="company-users/add" className="cp-add-btn">
                <IconPlus size={13} stroke={2.5} />
                Add Company User
              </Link>
            )
          }
        >
          {loadingCompanyUsers ? (
            <p className="cp-empty cp-empty--padded">Loading…</p>
          ) : errorCompanyUsers ? (
            <p className="cp-empty cp-empty--padded">Failed to load company users.</p>
          ) : companyUsers.length > 0 ? (
            <>
              <TableHeader showDelete={isAdmin} />
              {companyUsers.map((e) => (
                <PersonRow
                  key={e._id}
                  employee={e}
                  onDelete={isAdmin ? () => setPendingDelete({ id: e._id, name: `${e.firstName} ${e.surname}`, type: 'companyUser' }) : undefined}
                />
              ))}
            </>
          ) : (
            <p className="cp-empty cp-empty--padded">No company users added yet.</p>
          )}
        </SectionCard>

        {/* ── Site Supervisors ───────────────────────────────────────────── */}
        <SectionCard
          icon={<IconHelmet size={15} stroke={1.5} />}
          title="Site Supervisors"
          flush
          action={
            isAdmin && (
              <Link to="employees/add" className="cp-add-btn">
                <IconPlus size={13} stroke={2.5} />
                Add Supervisor
              </Link>
            )
          }
        >
          {loadingSupervisors ? (
            <p className="cp-empty cp-empty--padded">Loading…</p>
          ) : errorSupervisors ? (
            <p className="cp-empty cp-empty--padded">Failed to load supervisors.</p>
          ) : sortedSupervisors.length > 0 ? (
            <>
              <SupervisorTableHeader showActions={canResendInvite} />
              {sortedSupervisors.map((e) => (
                <SupervisorRow
                  key={e._id}
                  employee={e}
                  onResend={canResendInvite ? () => resendInvite({ userRoleId: e.userRole._id, email: e.contactEmail }) : undefined}
                  onDelete={isAdmin ? () => setPendingDelete({ id: e._id, name: `${e.firstName} ${e.surname}`, type: 'supervisor' }) : undefined}
                />
              ))}
            </>
          ) : (
            <p className="cp-empty cp-empty--padded">No supervisors added yet.</p>
          )}
        </SectionCard>

      </div>

    </div>
    <ConfirmModal
      confirm={deleteConfirm}
      onConfirm={handleConfirmDelete}
      onCancel={() => setPendingDelete(null)}
    />
    </>
  )
}
